import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import { WOD_PART_TYPE, WOD_PART_ELEMENT_TYPE, WOD_SCORE_MODE } from "../../enums.js";
// NOTE Seule la validation pure est importée : elle est partagée avec CfcWodController, qui revalide la découpe côté serveur
import { MAX_PARTS, getPartLabel, getDefaultPartName, normalizeParts, validateParts, normalizeScoreMode } from "../../validators/CfcWodPartValidator.js";

/** @typedef {import('../../validators/CfcWodPartValidator.js').CfcWodPartObj} CfcWodPartObj */

/**
 * @typedef {object} CfcWodPartsElementState
 * @property {number} key - Clé React de l'élément (non envoyée au serveur)
 * @property {string} type - Mouvement ou tie break
 * @property {string} name - Nom du mouvement
 * @property {string} quantity - Répétitions (AMRAP)
 * @property {string} objective - Objectif (FOR TIME)
 */

/**
 * @typedef {object} CfcWodPartsPartState
 * @property {string} name - Nom de la partie
 * @property {string} type - Format de la partie
 * @property {string} duration - Durée (AMRAP)
 * @property {string} rounds - Nombre de tours (AMRAP)
 * @property {string} time_cap - Time cap (FOR TIME)
 * @property {string} max_movement - Mouvement à maximiser (MAX WEIGHT)
 * @property {string} attempts - Nombre de tentatives (MAX WEIGHT)
 * @property {string} cash_in - Cash in
 * @property {string} cash_out - Cash out
 * @property {boolean} has_tie_break_after_cash_in - Tie break après le cash in
 * @property {Array<CfcWodPartsElementState>} elements - Mouvements et tie breaks
 */

let nextElementKey = 1;

/**
 * Crée un mouvement vide
 * @returns {CfcWodPartsElementState} Le mouvement
 */
function createMovement() {
  return { key: nextElementKey++, type: WOD_PART_ELEMENT_TYPE.MOVEMENT.value, name: "", quantity: "", objective: "" };
}

/**
 * Crée un tie break
 * @returns {CfcWodPartsElementState} Le tie break
 */
function createTieBreak() {
  return { key: nextElementKey++, type: WOD_PART_ELEMENT_TYPE.TIE_BREAK.value, name: "", quantity: "", objective: "" };
}

/**
 * Crée une partie vide avec deux mouvements (nom vide : le nom par défaut est donné à l'enregistrement)
 * @returns {CfcWodPartsPartState} La partie
 */
function createPart(type = WOD_PART_TYPE.AMRAP.value) {
  return {
    name: "",
    type,
    duration: "20 min",
    rounds: "20",
    time_cap: "",
    max_movement: "",
    attempts: "5",
    cash_in: "",
    cash_out: "",
    has_tie_break_after_cash_in: false,
    elements: [createMovement(), createMovement()]
  };
}

/**
 * Retire les clés React avant envoi au serveur
 * @param {Array<CfcWodPartsPartState>} parts - Parties saisies
 * @returns {Array<object>} Les parties à envoyer
 */
function serializeParts(parts) {
  return parts.map(part => ({ ...part, elements: part.elements.map(({ key, ...element }) => element) }));
}

/**
 * Formulaire d'une partie (A ou B) de la découpe du WOD
 * @param {object} props
 * @param {number} props.position - Position de la partie (1 = A, 2 = B)
 * @param {boolean} props.hasSeveralParts - Le WOD a plusieurs parties : la lettre et le nom de la partie ne sont affichés que dans ce cas
 * @param {CfcWodPartsPartState} props.part - Partie saisie
 * @param {(changes: Partial<CfcWodPartsPartState>) => void} props.onChange - Modification de la partie
 */
function CfcWodPartForm({ position, hasSeveralParts, part, onChange }) {
  const isAmrap = part.type === WOD_PART_TYPE.AMRAP.value;
  const isForTime = part.type === WOD_PART_TYPE.FOR_TIME.value;
  const isMaxWeight = part.type === WOD_PART_TYPE.MAX_WEIGHT.value;
  const partId = `cfc-wod-part-${position}`;

  /**
   * Modifie un élément de la partie
   * @param {number} key - Clé de l'élément
   * @param {Partial<CfcWodPartsElementState>} changes - Modifications
   */
  const changeElement = (key, changes) => {
    onChange({ elements: part.elements.map(element => (element.key === key ? { ...element, ...changes } : element)) });
  };

  return (
    <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-16 padding--16 border border--grey-300 cfcwodparts__part">
      {hasSeveralParts && (
        <div className="flexline flexline--no-mobile flexline--align-vz flexline--gap-16">
          <span className="flexline flexline--no-mobile flexline--align-vz flexline--align-hz flexline__item--shrink-zero cfcwodparts__letter">{getPartLabel(position)}</span>
          <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-8 cfcwodparts__part-name">
            <label htmlFor={`${partId}-name`} className="paragraph paragraph--small-font-size paragraph--uppercase">
              Partie {getPartLabel(position)} - nom affiché sur la fiche de score (facultatif)
            </label>
            <input type="text" id={`${partId}-name`} className="input-group__input" placeholder="Ex : Gymnastique" value={part.name} onChange={event => onChange({ name: event.target.value })} />
          </div>
        </div>
      )}

      <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-8">
        <span className="paragraph paragraph--small-font-size paragraph--uppercase">Format</span>
        <CfcWodPartsChoices options={Object.values(WOD_PART_TYPE)} value={part.type} onChange={type => onChange({ type })} />
      </div>

      {isAmrap && (
        <div className="grid grid--has-2-child">
          <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-8">
            <label htmlFor={`${partId}-duration`} className="paragraph paragraph--small-font-size paragraph--uppercase">
              Durée
            </label>
            <input type="text" id={`${partId}-duration`} className="input-group__input" placeholder="Ex : 20 min" value={part.duration} onChange={event => onChange({ duration: event.target.value })} />
          </div>
          <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-8">
            <label htmlFor={`${partId}-rounds`} className="paragraph paragraph--small-font-size paragraph--uppercase">
              Tours sur la fiche de score
            </label>
            <input type="number" min="1" max="100" id={`${partId}-rounds`} className="input-group__input" value={part.rounds} onChange={event => onChange({ rounds: event.target.value })} />
          </div>
        </div>
      )}
      {isForTime && (
        <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-8">
          <label htmlFor={`${partId}-timecap`} className="paragraph paragraph--small-font-size paragraph--uppercase">
            Time cap
          </label>
          <input type="text" id={`${partId}-timecap`} className="input-group__input" placeholder="Ex : 15:00" value={part.time_cap} onChange={event => onChange({ time_cap: event.target.value })} />
        </div>
      )}
      {isMaxWeight && (
        <div className="grid grid--has-2-child">
          <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-8">
            <label htmlFor={`${partId}-maxmovement`} className="paragraph paragraph--small-font-size paragraph--uppercase">
              * Mouvement (charge en kg)
            </label>
            <input type="text" id={`${partId}-maxmovement`} className="input-group__input" placeholder="Ex : Back Squat" value={part.max_movement} onChange={event => onChange({ max_movement: event.target.value })} />
          </div>
          <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-8">
            <label htmlFor={`${partId}-attempts`} className="paragraph paragraph--small-font-size paragraph--uppercase">
              Nombre de tentatives
            </label>
            <input type="number" min="1" max="20" id={`${partId}-attempts`} className="input-group__input" value={part.attempts} onChange={event => onChange({ attempts: event.target.value })} />
          </div>
        </div>
      )}

      <div className="grid grid--has-2-child">
        <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-8">
          <label htmlFor={`${partId}-cashin`} className="paragraph paragraph--small-font-size paragraph--uppercase">
            Cash in
          </label>
          <input type="text" id={`${partId}-cashin`} className="input-group__input" placeholder="Ex : 400 m Run" value={part.cash_in} onChange={event => onChange({ cash_in: event.target.value })} />
        </div>
        <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-8">
          <label htmlFor={`${partId}-cashout`} className="paragraph paragraph--small-font-size paragraph--uppercase">
            Cash out
          </label>
          <input type="text" id={`${partId}-cashout`} className="input-group__input" placeholder="Ex : 400 m Run" value={part.cash_out} onChange={event => onChange({ cash_out: event.target.value })} />
        </div>
      </div>
      <label htmlFor={`${partId}-tbcash`} className="flexline flexline--no-mobile flexline--align-vz flexline--gap-8 cfcwodparts__check">
        <input type="checkbox" id={`${partId}-tbcash`} className="input-group__checkbox" checked={part.has_tie_break_after_cash_in} disabled={!part.cash_in.trim()} onChange={event => onChange({ has_tie_break_after_cash_in: event.target.checked })} />
        Ajouter un Tie Break après le Cash In
      </label>

      {!isMaxWeight && (
        <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-8">
          <span className="paragraph paragraph--small-font-size paragraph--uppercase">Mouvements</span>
          {part.elements.map((element, index) => (
            <div className="flexline flexline--no-mobile flexline--align-vz flexline--gap-8" key={element.key}>
              {element.type === WOD_PART_ELEMENT_TYPE.TIE_BREAK.value ? (
                <span className="flexline flexline--no-mobile flexline--align-vz flexline--gap-8 padding--8 padding--hz-16 cfcwodparts__tiebreak">
                  <span className="material-icons">timer</span>TIE BREAK
                </span>
              ) : (
                <>
                  <input type="text" className="input-group__input" placeholder={`Nom du mouvement ${index + 1}`} value={element.name} onChange={event => changeElement(element.key, { name: event.target.value })} />
                  {isAmrap && <input type="number" min="1" className="input-group__input cfcwodparts__element-quantity" placeholder="Reps" value={element.quantity} onChange={event => changeElement(element.key, { quantity: event.target.value })} />}
                  {isForTime && <input type="text" className="input-group__input cfcwodparts__element-quantity" placeholder="Objectif" value={element.objective} onChange={event => changeElement(element.key, { objective: event.target.value })} />}
                </>
              )}
              <button type="button" className="btn btn--only-svg btn--only-svg-danger-color" data-tooltip="Supprimer" onClick={() => onChange({ elements: part.elements.filter(item => item.key !== element.key) })}>
                <span className="material-icons">delete</span>
              </button>
            </div>
          ))}
          <div className="flexline flexline--no-mobile flexline--wrap flexline--gap-8">
            <button type="button" className="btn btn--outline-secondary-color" onClick={() => onChange({ elements: [...part.elements, createMovement()] })}>
              <span className="material-icons">add</span>Mouvement
            </button>
            <button type="button" className="btn btn--outline-secondary-color" onClick={() => onChange({ elements: [...part.elements, createTieBreak()] })}>
              <span className="material-icons">timer</span>Tie Break
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Choix sous forme de boutons (même présentation que le simulateur de frais)
 * @param {object} props
 * @param {Array<{name: string, value: string}>} props.options - Choix proposés
 * @param {string} props.value - Choix sélectionné
 * @param {(value: string) => void} props.onChange - Sélection d'un choix
 */
function CfcWodPartsChoices({ options, value, onChange }) {
  return (
    <div className="flexline flexline--no-mobile flexline--wrap flexline--gap-8">
      {options.map(option => (
        <button type="button" key={option.value} className={`btn ${option.value === value ? "btn--main-color" : "btn--outline-secondary-color"}`} onClick={() => onChange(option.value)}>
          {option.name}
        </button>
      ))}
    </div>
  );
}

/**
 * Convertit les parties enregistrées en état du formulaire
 * @param {string | undefined} json - Parties au format JSON (attribut data-parts)
 * @returns {Array<CfcWodPartsPartState>} Les parties du formulaire
 */
function parseInitialParts(json) {
  try {
    const savedParts = JSON.parse(json || "[]");

    return savedParts.map((savedPart, index) => ({
      // NOTE Un nom par défaut (« WOD », « Partie A »...) n'est pas repris : il est recalculé à l'enregistrement selon le nombre de parties
      name: savedPart.name && savedPart.name !== getDefaultPartName(index + 1, savedParts.length) ? savedPart.name : "",
      type: savedPart.type,
      duration: savedPart.duration ?? "",
      rounds: savedPart.rounds != null ? String(savedPart.rounds) : "",
      time_cap: savedPart.time_cap ?? "",
      max_movement: savedPart.max_movement ?? "",
      attempts: savedPart.attempts != null ? String(savedPart.attempts) : "",
      cash_in: savedPart.cash_in ?? "",
      cash_out: savedPart.cash_out ?? "",
      has_tie_break_after_cash_in: Boolean(savedPart.has_tie_break_after_cash_in),
      elements: (savedPart.elements || []).map(element => ({
        key: nextElementKey++,
        type: element.type,
        name: element.name ?? "",
        quantity: element.quantity ?? "",
        objective: element.objective ?? ""
      }))
    }));
  } catch {
    return [];
  }
}

/**
 * Découpe d'un WOD en parties (formats, mouvements, cash in/out, tie breaks) saisie par l'organisateur.
 * Envoie la découpe au contrôleur via deux champs cachés du formulaire parent : wod_parts (JSON) et wod[score_mode].
 * @param {object} props
 * @param {Array<CfcWodPartsPartState>} props.initialParts - Parties déjà enregistrées (édition)
 * @param {string} props.initialScoreMode - Gestion du score déjà enregistrée (édition)
 */
function CfcWodParts({ initialParts, initialScoreMode }) {
  const [parts, setParts] = useState(initialParts);
  const [formPartType, setFormPartType] = useState(WOD_PART_TYPE.AMRAP.value);
  const [scoreMode, setScoreMode] = useState(initialScoreMode || WOD_SCORE_MODE.SEPARATE.value);
  const [errors, setErrors] = useState(/** @type {Array<string>} */ ([]));
  const rootRef = useRef(/** @type {?HTMLDivElement} */ (null));
  const latest = useRef({ parts });
  const isEnabled = parts.length > 0;
  const hasSeveralParts = parts.length > 1;
  const normalizedScoreMode = normalizeScoreMode(scoreMode, parts.length);

  latest.current = { parts };

  useEffect(() => {
    const form = rootRef.current?.closest("form");
    const typeSelect = /** @type {?HTMLSelectElement} */ (form?.querySelector('select[name="wod[type]"]'));

    if (!typeSelect) {
      return undefined;
    }

    const syncPartType = (updateParts = true) => {
      const partType = Object.values(WOD_PART_TYPE).find(option => option.value === typeSelect.value)?.value;

      if (!partType) {
        return;
      }

      setFormPartType(partType);
      if (updateParts) {
        setParts(currentParts => currentParts.map(part => ({ ...part, type: partType })));
      }
    };

    syncPartType(false);
    const onTypeChange = () => syncPartType();
    typeSelect.addEventListener("change", onTypeChange);

    return () => typeSelect.removeEventListener("change", onTypeChange);
  }, []);

  useEffect(() => {
    const form = rootRef.current?.closest("form");

    if (!form) {
      return undefined;
    }

    // NOTE Phase de capture : la découpe invalide bloque l'envoi avant les autres écouteurs du formulaire (loader du bouton...)
    const onSubmit = event => {
      const submitErrors = validateParts(normalizeParts(serializeParts(latest.current.parts)));

      setErrors(submitErrors);
      if (submitErrors.length) {
        event.preventDefault();
        event.stopImmediatePropagation();
        rootRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    };

    form.addEventListener("submit", onSubmit, true);
    return () => form.removeEventListener("submit", onSubmit, true);
  }, []);

  /**
   * Modifie une partie
   * @param {number} index - Index de la partie
   * @param {Partial<CfcWodPartsPartState>} changes - Modifications
   */
  const changePart = (index, changes) => {
    setParts(currentParts => currentParts.map((part, partIndex) => (partIndex === index ? { ...part, ...changes } : part)));
  };

  return (
    <div ref={rootRef} className="flexline flexline--no-mobile flexline--direction-column cfcwodparts">
      <input type="hidden" name="wod_parts" value={JSON.stringify(serializeParts(parts))} />
      {normalizedScoreMode && <input type="hidden" name="wod[score_mode]" value={normalizedScoreMode} />}

      <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-16 cfcwodparts__summary background--main-color">
        <span className="paragraph paragraph--small-font-size paragraph--uppercase paragraph--secondary-color paragraph--bold-700">Découpe du WOD</span>
        <label htmlFor="cfc-wod-parts-enabled" className="flexline flexline--no-mobile flexline--align-vz cfcwodparts__toggle">
          <input type="checkbox" id="cfc-wod-parts-enabled" className="input-group__checkbox" checked={isEnabled} onChange={event => setParts(event.target.checked ? [createPart(formPartType)] : [])} />
          Générer automatiquement la fiche de score et la saisie des scores dans l'app WEWOD des juges
        </label>
        {isEnabled ? (
          <div className="flexline flexline--no-mobile flexline--wrap flexline--gap-8">
            {parts.map((part, index) => (
              <span className="cfcwodparts__highlight" key={index}>
                {hasSeveralParts ? `Partie ${getPartLabel(index + 1)} · ` : ""}
                {part.type}
              </span>
            ))}
            {normalizedScoreMode && <span className="cfcwodparts__highlight">{Object.values(WOD_SCORE_MODE).find(mode => mode.value === normalizedScoreMode)?.name}</span>}
          </div>
        ) : (
          <span className="paragraph paragraph--small-font-size cfcwodparts__detail">Découpe désactivée : ce WOD n'aura ni fiche de score générée automatiquement, ni saisie des scores par les juges dans l'app WEWOD. Cochez la case pour décrire le format (AMRAP, FOR TIME, MAX WEIGHT), les mouvements, le cash in / cash out et les tie breaks : la fiche de score à imprimer et l'écran de saisie des scores des juges seront construits à partir de cette découpe. Si la découpe ne correspond pas à votre WOD, laissez-la décochée et décrivez-le dans la description.</span>
        )}
      </div>

      {isEnabled && (
        <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-24 cfcwodparts__form">
          {parts.map((part, index) => (
            <CfcWodPartForm key={index} position={index + 1} hasSeveralParts={hasSeveralParts} part={part} onChange={changes => changePart(index, changes)} />
          ))}

          <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-8">
            <span className="paragraph paragraph--small-font-size paragraph--uppercase">Nombre de parties</span>
            <CfcWodPartsChoices
              options={[
                { name: "Partie A uniquement", value: "1" },
                { name: "Parties A + B", value: String(MAX_PARTS) }
              ]}
              value={String(parts.length)}
              onChange={value => setParts(currentParts => (value === "1" ? currentParts.slice(0, 1) : currentParts.length >= MAX_PARTS ? currentParts : [...currentParts, createPart(formPartType)]))}
            />
          </div>

          {parts.length >= MAX_PARTS && (
            <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-8">
              <span className="paragraph paragraph--small-font-size paragraph--uppercase">Gestion du score</span>
              <CfcWodPartsChoices options={Object.values(WOD_SCORE_MODE)} value={scoreMode} onChange={setScoreMode} />
            </div>
          )}
        </div>
      )}

      {errors.length > 0 && (
        <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-24 cfcwodparts__form">
          <gw-alert data-style="danger">
            {errors.map(error => (
              <div key={error}>{error}</div>
            ))}
          </gw-alert>
        </div>
      )}
    </div>
  );
}
const cfcWodPartsNode = document.getElementById("cfc-wod-parts");

if (cfcWodPartsNode) {
  ReactDOM.createRoot(cfcWodPartsNode).render(<CfcWodParts initialParts={parseInitialParts(cfcWodPartsNode.dataset.parts)} initialScoreMode={cfcWodPartsNode.dataset.scoreMode} />);
}
